import {
  Actions,
  ApiEndpoints,
  APIMethods,
  PaginationLinks,
} from "@typeDefinitions";
import { fetchData } from "@utils/api";
import * as yup from "yup";
import { PaginationLinksSchema } from "@typeDefinitions/PaginationLinks";
import qs from "qs";
import { MetadataSchema, Metadata } from "@typeDefinitions/Metadata";

interface EducationalMaterialFilesQueryParams {
  page?: number;
  perPage?: number;
  query?: string;
  owner?: string;
  categoryId?: string;
  authorId?: string;
  reviewed?: boolean;
  favorite?: number;
  latest?: number;
}

export const fetchEducationalMaterialFiles = async (
  params?: EducationalMaterialFilesQueryParams,
): Promise<EducationalMaterialFilesResponse> => {
  const queryParams = qs.stringify(params, {
    skipNulls: true,
    arrayFormat: "brackets",
  });

  const response = await fetchData(
    `${ApiEndpoints.EducationalMaterialFilesSearch}?${queryParams}`,
    APIMethods.GET,
  );

  return await EducationalMaterialFilesResponseSchema.validate(response);
};

export interface EducationalMaterialImage {
  id: number;
  url: string;
}

export const EducationalMaterialImageSchema = yup.object({
  id: yup.number().required(),
  url: yup.string().required(),
});

export interface EducationalMaterialTranslation {
  lang: string;
  name: string | null;
  description: string | null;
}

export const EducationalMaterialTranslationSchema = yup.object({
  lang: yup.string().required(),
  name: yup.string().nullable().defined(),
  description: yup.string().nullable().defined(),
});

export const categorySchema = yup.object({
  id: yup.number().nullable().defined(),
});

export interface CategoryType {
  id: number | null;
}

export const ActionsSchema = yup.object({
  canEdit: yup.boolean().required(),
  canDelete: yup.boolean().required(),
  canVisible: yup.boolean().required(),
});

export interface EducationalMaterial {
  id: number;
  name: string | null;
  description: string | null;
  translations: EducationalMaterialTranslation[];
  image: EducationalMaterialImage | null;
  category: CategoryType | null;
  reviewed?: boolean | null;
  status?: number | null;
  shared?: boolean;
  uuid?: string | null;
}

export const EducationalMaterialSchema = yup.object({
  id: yup.number().required(),
  name: yup.string().nullable().defined(),
  description: yup.string().nullable().defined(),
  translations: yup.array().of(EducationalMaterialTranslationSchema).required(),
  image: EducationalMaterialImageSchema.nullable(),
  category: categorySchema.nullable(),
  reviewed: yup.boolean().nullable(),
  uuid: yup.string().nullable(),
});

export const EducationalMaterialFilesMetaSchema = yup.object({
  current_page: yup.number().required(),
  from: yup.number().required(),
  last_page: yup.number().required(),
  path: yup.string().required(),
  per_page: yup.number().required(),
  to: yup.number().required(),
  total: yup.number().required(),
});

export const EducationalMaterialDataSchema = yup.object({
  resource: EducationalMaterialSchema.required(),
  actions: ActionsSchema.required(),
});

interface EducationalMaterialData {
  resource: EducationalMaterial;
  actions: Actions;
}

export interface EducationalMaterialFilesResponse {
  data: EducationalMaterialData[];
  links: PaginationLinks;
  meta: Metadata;
}

export const EducationalMaterialFilesResponseSchema = yup.object({
  data: yup.array().of(EducationalMaterialDataSchema).required(),
  links: PaginationLinksSchema.required(),
  meta: MetadataSchema.required(),
});
